import React, { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'urp-theme';

/**
 * Theme control for the portals.
 *
 * Sets `data-theme` on <html>, which flips the semantic tokens in App.css.
 * Three states rather than two: 'system' follows the OS, so someone whose
 * phone switches to dark at sunset gets it without touching this — and it is
 * the correct DEFAULT, because guessing wrong is worse than following the
 * signal the user already gave their device.
 *
 * IMPORTANT: only surfaces whose colours are TOKENS may be flipped. As of
 * 2026-07-26 that is the photographer portal. The client portal and admin still
 * carry ~1,750 hardcoded hex values, so flipping them produces dark chrome
 * around white cards with black text. See HANDOFF before extending this.
 */
export function applyTheme(mode) {
  const root = document.documentElement;
  const effective = mode === 'system'
    ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode;

  if (effective === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
  return effective;
}

export function getStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'system';
  } catch (e) {
    return 'system';
  }
}

/**
 * Applied before React mounts, from index.js. Without this the page paints in
 * light and then snaps to dark once the component mounts — a white flash on
 * every load, which is exactly what someone enabling dark mode is trying to
 * avoid.
 */
export function initTheme() {
  applyTheme(getStoredTheme());
}

function ThemeToggle({ compact = false }) {
  const [mode, setMode] = useState(getStoredTheme);

  useEffect(() => {
    applyTheme(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch (e) { /* private mode */ }
  }, [mode]);

  // Track the OS while in 'system' mode so a sunset switch takes effect live.
  useEffect(() => {
    if (mode !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode((m) => (m === 'system' ? 'light' : m === 'light' ? 'dark' : 'system'));
  }, []);

  const label = mode === 'system' ? 'Auto' : mode === 'dark' ? 'Dark' : 'Light';
  const icon = mode === 'system' ? '◐' : mode === 'dark' ? '☾' : '☀';

  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${label} — click to change`}
      aria-label={`Theme: ${label}. Click to change.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        background: 'transparent',
        border: '1px solid var(--border-strong, #d1d5db)',
        color: 'var(--text-muted, #6b7280)',
        borderRadius: '6px',
        padding: compact ? '5px 9px' : '6px 12px',
        fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
        lineHeight: 1
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '0.95rem' }}>{icon}</span>
      {!compact && <span>{label}</span>}
    </button>
  );
}

export default ThemeToggle;
